import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import {
	Button,
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	Flex,
	Text,
	TextAreaField,
} from "@backstage/ui";
import {
	AVD_PLUGIN_ID,
	BUNDLE_ARCHIVE_REASON_MAX_LENGTH,
	bundleArchivePath,
	bundleArchiveRequestErrors,
	canArchive,
	type BundleArchiveRequest,
	type BundleInventoryItem,
} from "@internal/backstage-plugin-avd-common";
import { ReactNode, useCallback, useRef, useState } from "react";

import { absoluteTime } from "./inventoryCells";

/** The server's own refusal (a Backstage error body) where it gave one, else a status line. */
async function refusal(res: Response): Promise<string> {
	try {
		const body = (await res.json()) as { error?: { message?: unknown } };
		if (typeof body.error?.message === "string") return body.error.message;
	} catch {
		// Not JSON: fall through to the status.
	}
	return `The registry row could not be changed (HTTP ${res.status}).`;
}

/** One call to the archive route, resolved to an error message or `undefined` on success. Never throws. */
function useArchiveRequest(item: BundleInventoryItem) {
	const discoveryApi = useApi(discoveryApiRef);
	const fetchApi = useApi(fetchApiRef);
	return useCallback(
		async (
			method: "PUT" | "DELETE",
			body?: BundleArchiveRequest,
		): Promise<string | undefined> => {
			try {
				const baseUrl = await discoveryApi.getBaseUrl(AVD_PLUGIN_ID);
				const res = await fetchApi.fetch(
					`${baseUrl}${bundleArchivePath(item.bundleType, item.slug, item.bundleId)}`,
					{
						method,
						headers: body ? { "Content-Type": "application/json" } : undefined,
						body: body ? JSON.stringify(body) : undefined,
					},
				);
				return res.ok ? undefined : await refusal(res);
			} catch {
				return "The platform could not be reached. Try again.";
			}
		},
		[discoveryApi, fetchApi, item.bundleType, item.slug, item.bundleId],
	);
}

/** The reason form: PUTs the archive, then closes and reports; the server's refusal stays on screen. */
function ArchiveDialog(
	props: Readonly<{
		open: boolean;
		title: string;
		onCancel: () => void;
		onSubmit: (request: BundleArchiveRequest) => Promise<string | undefined>;
	}>,
): ReactNode {
	const [reason, setReason] = useState("");
	const [error, setError] = useState<string>();
	const [busy, setBusy] = useState(false);

	const submit = async () => {
		const request = { reason: reason.trim() };
		const errors = bundleArchiveRequestErrors(request);
		if (errors.length > 0) {
			setError(errors.join("; "));
			return;
		}
		setBusy(true);
		const refused = await props.onSubmit(request);
		setBusy(false);
		if (refused) setError(refused);
		else setReason("");
	};

	return (
		<Dialog
			isOpen={props.open}
			onOpenChange={(isOpen) => {
				if (!isOpen && !busy) props.onCancel();
			}}
			width={460}
		>
			<DialogHeader>{props.title}</DialogHeader>
			<DialogBody>
				<Flex direction="column" gap="3">
					<Text as="p" color="secondary">
						The row is hidden from the inventory and the attention list. Provisioning
						this colleague again brings it back, and so does Restore.
					</Text>
					<TextAreaField
						label="Reason"
						description="For example: canary run, or a test identity."
						value={reason}
						onChange={setReason}
						maxLength={BUNDLE_ARCHIVE_REASON_MAX_LENGTH}
						rows={3}
						isRequired
					/>
					{error ? (
						<Text as="p" color="danger" role="alert">
							{error}
						</Text>
					) : null}
				</Flex>
			</DialogBody>
			<DialogFooter>
				<Button variant="secondary" onPress={props.onCancel} isDisabled={busy}>
					Cancel
				</Button>
				<Button variant="primary" onPress={submit} isPending={busy}>
					Archive row
				</Button>
			</DialogFooter>
		</Dialog>
	);
}

/** An admin's archive controls for one registry row: archive a removed bundle's row, or restore it. */
export function AdminBundleArchive(
	props: Readonly<{ item: BundleInventoryItem; onChanged?: () => void }>,
): ReactNode {
	const { item, onChanged } = props;
	const request = useArchiveRequest(item);
	const [open, setOpen] = useState(false);
	const [error, setError] = useState<string>();
	// Nothing hands focus back (the form has no DialogTrigger); without it Escape misses the sheet.
	const archiveRef = useRef<HTMLButtonElement>(null);
	const closeForm = () => {
		setOpen(false);
		requestAnimationFrame(() => archiveRef.current?.focus());
	};
	const restore = async () => {
		const refused = await request("DELETE");
		setError(refused);
		if (!refused) onChanged?.();
	};

	if (item.archivedAt) {
		return (
			<Flex direction="column" gap="2">
				<Text as="p" variant="body-medium">
					{`Archived ${absoluteTime(item.archivedAt)}${item.archivedBy ? ` by ${item.archivedBy}` : ""}${item.archiveReason ? `: ${item.archiveReason}` : ""}`}
				</Text>
				<Flex gap="2">
					<Button size="small" variant="secondary" onPress={restore}>
						Restore
					</Button>
				</Flex>
				{error ? (
					<Text as="p" color="danger" role="alert">
						{error}
					</Text>
				) : null}
			</Flex>
		);
	}

	if (!canArchive(item.status)) return null;

	return (
		<>
			<Flex direction="column" gap="2">
				<Text as="p" variant="body-medium">
					This desktop is gone. Archive the registry row to take it off the inventory.
				</Text>
				<Flex gap="2">
					<Button
						ref={archiveRef}
						size="small"
						variant="secondary"
						onPress={() => setOpen(true)}
					>
						Archive row
					</Button>
				</Flex>
			</Flex>
			<ArchiveDialog
				open={open}
				title={`Archive ${item.slug}'s ${item.bundleType} row`}
				onCancel={closeForm}
				onSubmit={async (body) => {
					const refused = await request("PUT", body);
					if (!refused) {
						closeForm();
						onChanged?.();
					}
					return refused;
				}}
			/>
		</>
	);
}
